// src/components/AddPositionForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  MenuItem,
  Divider,
  Checkbox,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { useAuthContext } from "../context/AuthContext";
import { ACTIONS, DEFAULT_PERMISSION_CATALOG } from "../hooks/usePositions";

// ⚠️ Importa permissões efetivas do usuário logado
import { useEffectivePermissions, toSet, can } from "../hooks/useEffectivePermissions";

const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");
const n = (v) => (v === "" || v == null ? null : Number(v));
const LABELS = { view: "Ver", create: "Criar", update: "Editar", delete: "Excluir", export: "Exportar" };

/** Lê hints de roteamento/recepcionista vindos da API (`routingHints` ou JSON legado). */
function parseRoutingHintsFromPosition(row) {
  const raw = row?.routingHints ?? row?.routing_hints_json;
  if (raw == null || raw === "") {
    return { routingAliases: "", routingNeeds: "", routingNotes: "" };
  }
  let o = raw;
  if (typeof raw === "string") {
    try {
      o = JSON.parse(raw);
    } catch {
      return { routingAliases: "", routingNeeds: "", routingNotes: "" };
    }
  }
  if (!o || typeof o !== "object") {
    return { routingAliases: "", routingNeeds: "", routingNotes: "" };
  }
  return {
    routingAliases: o.customerRoleAliases != null ? String(o.customerRoleAliases) : "",
    routingNeeds: o.needKeywords != null ? String(o.needKeywords) : "",
    routingNotes: o.receptionistNotes != null ? String(o.receptionistNotes) : "",
  };
}

function positionHasRoutingHints(row) {
  const h = parseRoutingHintsFromPosition(row);
  return Boolean(h.routingAliases.trim() || h.routingNeeds.trim() || h.routingNotes.trim());
}

/** ==== Helpers para preservar escopos desconhecidos (fora do catálogo) ==== */
const splitKnownUnknownScopes = (scopes = [], catalog) => {
  const knownKeys = new Set((catalog || []).flatMap((m) => (m.resources || []).map((r) => r.key)));
  const known = [];
  const unknown = [];
  for (const raw of scopes) {
    const [res, act] = String(raw).split(".");
    if (!res || !act) continue;
    (knownKeys.has(res) ? known : unknown).push(`${res}.${act}`);
  }
  return { known, unknown };
};

const mergeScopes = (knownSet, unknownArr) => {
  const out = new Set([...(unknownArr || [])]);
  for (const k of Array.from(knownSet || [])) out.add(k);
  return Array.from(out);
};

/* ================= Permission Matrix (módulos → recursos → ações) ================ */
/* ================= Permission Matrix (módulos → recursos → ações) ================ */
const PermissionMatrix = ({
  catalog = DEFAULT_PERMISSION_CATALOG,
  scopesSet,              // estado atual (editável) do cargo
  onToggle,               // (res, act, enabled) -> void (internamente já bloqueia itens não permitidos)
  allowedSet,             // Set com escopos que o usuário PODE alterar (tem acesso)
  immutableSet,           // Set com escopos inicialmente setados no cargo que o usuário NÃO tem (mostra desabilitado)
}) => {
  const actsFor = (r) => (Array.isArray(r?.actions) && r.actions.length ? r.actions : ACTIONS);
  const has = (resKey, act) => scopesSet.has(`${resKey}.${act}`);

  // Um escopo é "visível" se o usuário pode mexer nele (allowed) OU se ele é imutável (está no cargo, mas usuário não tem)
  const isVisible = (resKey, act) => {
    const key = `${resKey}.${act}`;
    return allowedSet.has(key) || immutableSet.has(key);
  };
  // Um escopo é "bloqueado" se está visível por ser imutável (usuário não tem)
  const isLocked = (resKey, act) => {
    const key = `${resKey}.${act}`;
    return !allowedSet.has(key) && immutableSet.has(key);
  };

  // estados de UI
  const [expanded, setExpanded] = useState(() => new Set(catalog.map((m) => m.module)));
  const [search, setSearch] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);

  const toggleExpanded = (moduleName) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(moduleName) ? next.delete(moduleName) : next.add(moduleName);
      return next;
    });

  const expandAll = () => setExpanded(new Set(catalog.map((m) => m.module)));
  const collapseAll = () => setExpanded(new Set());

  // helpers considerando apenas ações visíveis
  const visibleActsFor = (r) => actsFor(r).filter((a) => isVisible(r.key, a));
  const allowedActsFor = (r) => actsFor(r).filter((a) => allowedSet.has(`${r.key}.${a}`));

  const allActs = (r) => {
    const visibles = visibleActsFor(r);
    return visibles.length > 0 && visibles.every((a) => has(r.key, a));
  };

  const someActs = (r) => {
    const visibles = visibleActsFor(r);
    const checked = visibles.filter((a) => has(r.key, a)).length;
    return checked > 0 && checked < visibles.length;
  };

  const toggleResourceAll = (r) => {
    const allowed = allowedActsFor(r);
    if (allowed.length === 0) return;
    const enable = !allowed.every((a) => has(r.key, a));
    allowed.forEach((a) => onToggle(r.key, a, enable));
  };

  const columnState = (mod, action) => {
    // considera apenas recursos que realmente exibem essa ação
    const eligible = (mod.resources || []).filter((r) => Array.isArray(r.actions) && r.actions.includes(action));
    const total = eligible.length;
    const checked = eligible.filter((r) => has(r.key, action)).length;
    return { total, checked, all: total > 0 && checked === total, some: total > 0 && checked > 0 && checked < total };
  };

  const toggleColumn = (mod, action) => {
    const eligible = (mod.resources || []).filter((r) => Array.isArray(r.actions) && r.actions.includes(action));
    const allowed = eligible.filter((r) => allowedSet.has(`${r.key}.${action}`));
    if (allowed.length === 0) return;
    const allAllowedChecked = allowed.every((r) => has(r.key, action));
    const enable = !allAllowedChecked;
    allowed.forEach((r) => onToggle(r.key, action, enable));
  };

  const moduleCounts = (mod) => {
    // conta apenas ações visíveis no próprio módulo
    let total = 0;
    let selected = 0;
    (mod.resources || []).forEach((r) => {
      const acts = Array.isArray(r.actions) ? r.actions : [];
      total += acts.length;
      acts.forEach((a) => { if (has(r.key, a)) selected++; });
    });
    return { total, selected };
  };

  // Filtra catálogo (texto + somente selecionados) E calcula, por módulo, as ações visíveis (para esconder colunas vazias)
  const filterCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (catalog || [])
      .map((mod) => {
        const visibleActionSet = new Set();
        const filteredResources = (mod.resources || [])
          .map((r) => {
            const visActs = visibleActsFor(r);
            if (visActs.length === 0) return null;

            const matchesQ = !q || r.label.toLowerCase().includes(q) || r.key.toLowerCase().includes(q);
            const selectedAny = visActs.some((a) => has(r.key, a));
            if (!matchesQ || (onlySelected && !selectedAny)) return null;

            visActs.forEach((a) => visibleActionSet.add(a));
            return { ...r, actions: visActs }; // trava ações do recurso no que é visível
          })
          .filter(Boolean);

        const visibleActions = Array.from(visibleActionSet); // ordem conforme inserção
        return { ...mod, resources: filteredResources, visibleActions };
      })
      .filter((m) => (m.resources || []).length > 0);
  }, [catalog, search, onlySelected, scopesSet, allowedSet, immutableSet]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Barra de ferramentas da matriz */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Buscar recurso…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <FormControlLabel
          control={<Switch checked={onlySelected} onChange={(e) => setOnlySelected(e.target.checked)} />}
          label="Mostrar apenas selecionados"
        />
        <Button size="small" onClick={expandAll}>Expandir tudo</Button>
        <Button size="small" onClick={collapseAll}>Recolher tudo</Button>
      </Box>

      {filterCatalog.map((mod) => {
        const isOpen = expanded.has(mod.module);
        const { total, selected } = moduleCounts(mod);
        const visibleActions = mod.visibleActions || []; // ações com pelo menos 1 célula no módulo

        return (
          <Paper key={mod.module} variant="outlined" sx={{ p: 2 }}>
            {/* Cabeçalho do módulo */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Tri-state geral do módulo (apenas allowed/visíveis) */}
                <Checkbox
                  checked={selected > 0 && selected === total}
                  indeterminate={selected > 0 && selected < total}
                  onChange={() => {
                    const allAllowed = (mod.resources || []).flatMap((r) =>
                      allowedActsFor(r).map((a) => ({ r, a }))
                    );
                    if (allAllowed.length === 0) return;
                    const allAllowedChecked = allAllowed.every(({ r, a }) => has(r.key, a));
                    const enable = !allAllowedChecked;
                    allAllowed.forEach(({ r, a }) => onToggle(r.key, a, enable));
                  }}
                />
                <Typography variant="subtitle1">{mod.module}</Typography>
                <Chip size="small" label={`${selected}/${total}`} />
              </Box>

              {/* Selecionar por coluna no módulo – só as colunas com conteúdo */}
              {visibleActions.length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${visibleActions.length}, auto)`, alignItems: "center", gap: 2 }}>
                  {visibleActions.map((a) => {
                    const cs = columnState(mod, a);
                    return (
                      <Box key={`${mod.module}-${a}`} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Checkbox
                          size="small"
                          disabled={cs.total === 0}
                          checked={cs.all}
                          indeterminate={cs.some}
                          onChange={() => toggleColumn(mod, a)}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {LABELS[a] ?? a}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Button size="small" onClick={() => toggleExpanded(mod.module)}>
                {isOpen ? "Recolher" : "Expandir"}
              </Button>
            </Box>

            <Divider />

            {/* Tabela do módulo */}
            <Collapse in={isOpen} unmountOnExit>
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {/* header visual de colunas */}
                <Grid item xs={12} md={3} />
                {visibleActions.length > 0 && (
                  <Grid item xs={12} md={9}>
                    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${visibleActions.length}, 1fr)` }}>
                      {visibleActions.map((a) => (
                        <Typography key={a} variant="caption" sx={{ textTransform: "uppercase", fontWeight: 600 }}>
                          {LABELS[a] ?? a}
                        </Typography>
                      ))}
                    </Box>
                  </Grid>
                )}

                {(mod.resources || []).map((r) => (
                  <React.Fragment key={r.key}>
                    {/* Recurso + checkbox all (apenas para allowed) */}
                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Checkbox
                          checked={allActs(r)}
                          indeterminate={someActs(r)}
                          onChange={() => toggleResourceAll(r)}
                        />
                        <Typography variant="body2">{r.label}</Typography>
                      </Box>
                    </Grid>

                    {/* Ações (somente colunas com conteúdo no módulo) */}
                    {visibleActions.length > 0 && (
                      <Grid item xs={12} md={9}>
                        <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${visibleActions.length}, 1fr)` }}>
                          {visibleActions.map((a) => {
                            const key = `${r.key}.${a}`;
                            const actionVisibleInResource = Array.isArray(r.actions) && r.actions.includes(a);
                            if (!actionVisibleInResource) {
                              return <span key={a} />; // mantém alinhamento
                            }
                            const allowed = allowedSet.has(key);
                            const locked = isLocked(r.key, a);
                            const checked = has(r.key, a);

                            return (
                              <Tooltip
                                key={a}
                                title={
                                  locked
                                    ? "Permissão já setada neste cargo. Você não possui este escopo para alterá-la."
                                    : allowed
                                    ? ""
                                    : "Indisponível"
                                }
                              >
                                <span>
                                  <Checkbox
                                    disabled={!allowed}
                                    checked={!!checked}
                                    onChange={(e) => onToggle(r.key, a, e.target.checked)}
                                    sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                                  />
                                </span>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Grid>
                    )}
                  </React.Fragment>
                ))}
              </Grid>
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
};

/* =========================== Formulário =========================== */
const AddPositionForm = ({ data, catalog, allPositions = [], onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();
  const isMasterUser = Number(userData?.master) === 1;

  // permissões efetivas do usuário logado
  const { data: permData } = useEffectivePermissions();
  const userScopeSet = useMemo(() => toSet(permData?.scopes || []), [permData?.scopes]);

  const [errorMessage, setErrorMessage] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(() => positionHasRoutingHints(data));

  // Inicialização levando em conta catálogo (preserva desconhecidos)
  const initialScopesRaw = useMemo(() => {
    return Array.isArray(data?.permissions) ? data.permissions : (Array.isArray(data?.scopes) ? data.scopes : []);
  }, [data?.id]);

  const { known: initialKnown, unknown: initialUnknown } = useMemo(
    () => splitKnownUnknownScopes(initialScopesRaw, catalog),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.id, JSON.stringify(catalog)]
  );

  // Set(s) essenciais
  const initialKnownSet = useMemo(() => new Set(initialKnown), [initialKnown]);

  const routingInit = parseRoutingHintsFromPosition(data);
  const [form, setForm] = useState({
    ...data,
    nome: data?.nome || "",
    descricao: data?.descricao || "",
    ordem: data?.ordem ?? "",
    status: data?.status == null ? 1 : Number(data.status),
    id_usuario: data?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
    routingAliases: routingInit.routingAliases,
    routingNeeds: routingInit.routingNeeds,
    routingNotes: routingInit.routingNotes,
  });

  const [scopesSet, setScopesSet] = useState(new Set(initialKnown));
  const [unknownScopes, setUnknownScopes] = useState(initialUnknown);
  const [cloneFromId, setCloneFromId] = useState("");

  // Conjunto de escopos que o usuário PODE alterar (tem o mesmo escopo)
  const allowedSet = useMemo(() => {
    const out = new Set();
    const cat = Array.isArray(catalog) && catalog.length ? catalog : DEFAULT_PERMISSION_CATALOG;
    cat.forEach((mod) =>
      mod.resources.forEach((r) => {
        const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
        acts.forEach((a) => {
          if (isMasterUser || can(userScopeSet, r.key, a)) out.add(`${r.key}.${a}`);
        });
      })
    );
    return out;
  }, [catalog, userScopeSet, isMasterUser]);

  // Escopos IMUTÁVEIS: estão setados no cargo, mas o usuário não tem permissão => aparecem, mas desabilitados
  const immutableSet = useMemo(() => {
    const out = new Set();
    initialKnownSet.forEach((k) => {
      if (!allowedSet.has(k)) out.add(k);
    });
    return out;
  }, [initialKnownSet, allowedSet]);

  useEffect(() => {
    const rh = parseRoutingHintsFromPosition(data);
    setForm((p) => ({
      ...p,
      ...data,
      nome: data?.nome || "",
      descricao: data?.descricao || "",
      ordem: data?.ordem ?? "",
      status: data?.status == null ? 1 : Number(data.status),
      id_usuario: data?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      routingAliases: rh.routingAliases,
      routingNeeds: rh.routingNeeds,
      routingNotes: rh.routingNotes,
    }));

    const raw = Array.isArray(data?.permissions) ? data.permissions : (Array.isArray(data?.scopes) ? data.scopes : []);
    const { known, unknown } = splitKnownUnknownScopes(raw, catalog);
    setScopesSet(new Set(known));
    setUnknownScopes(unknown);

    setCloneFromId("");
    if (positionHasRoutingHints(data)) setShowAdvanced(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, JSON.stringify(catalog)]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Bloqueia toggles em escopos não permitidos; preserva imutáveis
  const toggleScope = (resource, action, enabled) => {
    const key = `${resource}.${action}`;
    if (!allowedSet.has(key)) return; // sem acesso → não altera
    setScopesSet((prev) => {
      const next = new Set(prev);
      if (enabled) next.add(key);
      else {
        // não permitir remover se for imutável (mas imutável nunca cai aqui pois !allowed, de qualquer forma fica protegido)
        next.delete(key);
      }
      return next;
    });
  };

  const cloneFrom = (id) => {
    setCloneFromId(id);
    const src = allPositions.find((p) => String(p.id) === String(id));
    if (!src) return;
    const raw = Array.isArray(src.permissions) ? src.permissions : (Array.isArray(src.scopes) ? src.scopes : []);
    const { known /*, unknown*/ } = splitKnownUnknownScopes(raw, catalog);

    // Aplica apenas o que o usuário pode alterar. Mantém escopos imutáveis originais do cargo sendo editado.
    const next = new Set(scopesSet);
    // zera somente os que pode mexer
    for (const k of Array.from(next)) {
      if (allowedSet.has(k)) next.delete(k);
    }
    // adiciona do clone apenas os allowed
    known.forEach((k) => {
      if (allowedSet.has(k)) next.add(k);
    });
    setScopesSet(next);

    // unknownScopes do cargo atual permanecem
    setUnknownScopes((prev) => prev);
  };

  const buildPayload = () => {
    // Garante que imutáveis sempre estejam no payload
    const combinedEditable = new Set(scopesSet);
    immutableSet.forEach((k) => combinedEditable.add(k));
    const combinedScopes = mergeScopes(combinedEditable, unknownScopes);
    const ra = (form.routingAliases != null ? String(form.routingAliases) : "").trim();
    const rn = (form.routingNeeds != null ? String(form.routingNeeds) : "").trim();
    const ro = (form.routingNotes != null ? String(form.routingNotes) : "").trim();
    const routing_hints =
      ra || rn || ro
        ? { customerRoleAliases: ra, needKeywords: rn, receptionistNotes: ro }
        : null;

    return {
      id: form.id,
      nome: (form.nome || "").trim(),
      descricao: form.descricao || "",
      ordem: n(digitsOnly(form.ordem)),
      status: Number(form.status) === 1 ? 1 : 0,
      id_usuario: form.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      permissions: combinedScopes,
      scopes: combinedScopes,
      routing_hints,
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      const payload = buildPayload();
      if (!payload.nome) throw new Error("Nome é obrigatório.");
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao salvar o cargo.");
    }
  };

  const totalVisibleSelected = useMemo(() => {
    // conta apenas o que é visível (allowed ∪ immutable) dentro do catálogo
    const cat = Array.isArray(catalog) && catalog.length ? catalog : DEFAULT_PERMISSION_CATALOG;
    let selected = 0;
    let total = 0;
    cat.forEach((mod) =>
      mod.resources.forEach((r) => {
        const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
        acts.forEach((a) => {
          const key = `${r.key}.${a}`;
          const visible = allowedSet.has(key) || immutableSet.has(key);
          if (visible) {
            total++;
            if (scopesSet.has(key) || immutableSet.has(key)) selected++;
          }
        });
      })
    );
    return { selected, total };
  }, [catalog, scopesSet, allowedSet, immutableSet]);

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {form?.id ? "Editar Cargo" : "Adicionar Cargo"}
      </Typography>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <form onSubmit={submit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <TextField
              label="Nome"
              name="nome"
              value={form.nome}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(form.status) === 1}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))}
                />
              }
              label={Number(form.status) === 1 ? "Ativo" : "Inativo"}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição"
              name="descricao"
              value={form.descricao}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="button"
              variant="text"
              size="small"
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{ textTransform: "none", px: 0, color: "text.secondary" }}
            >
              {showAdvanced ? "Ocultar opções avançadas" : "Opções avançadas (recepcionista / IA)"}
            </Button>
            <Collapse in={showAdvanced}>
              <Alert severity="info" sx={{ mb: 1.5, mt: 0.5 }}>
                Estes campos são opcionais. Eles orientam a recepcionista virtual ao encaminhar o cliente para um
                atendente humano deste cargo (sinónimos de cargo, tipos de necessidade e notas internas).
              </Alert>
              <TextField
                label="Sinónimos de cargo do cliente"
                name="routingAliases"
                value={form.routingAliases ?? ""}
                onChange={handleChange}
                fullWidth
                margin="dense"
                multiline
                minRows={2}
                placeholder="gerente, gestor comercial, coordenador de loja"
                helperText="Termos que o cliente pode usar para se identificar; separe por vírgula."
              />
              <TextField
                label="Necessidades / temas atendidos por este cargo"
                name="routingNeeds"
                value={form.routingNeeds ?? ""}
                onChange={handleChange}
                fullWidth
                margin="dense"
                multiline
                minRows={2}
                placeholder="financeiro, faturamento, troca de nota fiscal"
                helperText="Palavras-chave do pedido que combinam com este perfil."
              />
              <TextField
                label="Notas para a recepcionista (IA)"
                name="routingNotes"
                value={form.routingNotes ?? ""}
                onChange={handleChange}
                fullWidth
                margin="dense"
                multiline
                minRows={2}
                placeholder="Ex.: priorizar quando o cliente mencionar contrato corporativo."
                helperText="Texto livre; não é exibido ao cliente final."
              />
            </Collapse>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Barra de clone e resumo */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
          <TextField
            select
            label="Clonar permissões de"
            value={cloneFromId}
            onChange={(e) => cloneFrom(e.target.value)}
            sx={{ minWidth: 280 }}
          >
            <MenuItem value="">—</MenuItem>
            {allPositions
              .filter((p) => String(p.id) !== String(form.id))
              .map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
              ))}
          </TextField>

          <Chip label={`${totalVisibleSelected.selected}/${totalVisibleSelected.total} visíveis selecionados`} />

          <Tooltip title="Copiar escopos (inclui imutáveis) para a área de transferência">
            <IconButton
              size="small"
              onClick={() => {
                const combinedEditable = new Set(scopesSet);
                immutableSet.forEach((k) => combinedEditable.add(k));
                navigator?.clipboard?.writeText(mergeScopes(combinedEditable, unknownScopes).join(","));
              }}
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Presets rápidos — respeitam allowedSet e preservam imutáveis */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Tooltip title="Marcar apenas VER em todos os recursos (somente onde você tem acesso)">
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const next = new Set(scopesSet);
                // limpa apenas allowed
                for (const k of Array.from(next)) {
                  if (allowedSet.has(k)) next.delete(k);
                }
                const cat = Array.isArray(catalog) && catalog.length ? catalog : DEFAULT_PERMISSION_CATALOG;
                cat.forEach((mod) =>
                  mod.resources.forEach((r) => {
                    const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
                    if (acts.includes("view")) {
                      const key = `${r.key}.view`;
                      if (allowedSet.has(key)) next.add(key);
                    }
                  })
                );
                setScopesSet(next);
              }}
            >
              Somente ver
            </Button>
          </Tooltip>

          <Tooltip title="VER + CRIAR + EDITAR (apenas nos escopos que você possui)">
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const next = new Set(scopesSet);
                for (const k of Array.from(next)) {
                  if (allowedSet.has(k)) next.delete(k);
                }
                const cat = Array.isArray(catalog) && catalog.length ? catalog : DEFAULT_PERMISSION_CATALOG;
                cat.forEach((mod) =>
                  mod.resources.forEach((r) => {
                    const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
                    ["view", "create", "update"].forEach((a) => {
                      if (acts.includes(a)) {
                        const key = `${r.key}.${a}`;
                        if (allowedSet.has(key)) next.add(key);
                      }
                    });
                  })
                );
                setScopesSet(next);
              }}
            >
              Editor
            </Button>
          </Tooltip>

          <Tooltip title="Todas as ações em todos os recursos (apenas onde você tem acesso)">
          <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const next = new Set(scopesSet);
                for (const k of Array.from(next)) {
                  if (allowedSet.has(k)) next.delete(k);
                }
                const cat = Array.isArray(catalog) && catalog.length ? catalog : DEFAULT_PERMISSION_CATALOG;
                cat.forEach((mod) =>
                  mod.resources.forEach((r) => {
                    const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
                    acts.forEach((a) => {
                      const key = `${r.key}.${a}`;
                      if (allowedSet.has(key)) next.add(key);
                    });
                  })
                );
                setScopesSet(next);
              }}
            >
              Admin
            </Button>
          </Tooltip>

          <Tooltip title="Limpar permissões (mantém as imutáveis e só limpa o que você pode alterar)">
            <Button
              size="small"
              onClick={() => {
                const next = new Set(scopesSet);
                for (const k of Array.from(next)) {
                  if (allowedSet.has(k)) next.delete(k);
                }
                setScopesSet(next);
              }}
            >
              Limpar
            </Button>
          </Tooltip>
        </Box>

        {/* Matriz de Permissões */}
        <PermissionMatrix
          catalog={Array.isArray(catalog) && catalog.length ? catalog : DEFAULT_PERMISSION_CATALOG}
          scopesSet={scopesSet}
          onToggle={toggleScope}
          allowedSet={allowedSet}
          immutableSet={immutableSet}
        />

        {/* Ações */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AddPositionForm;
